import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  saveChatMessage,
  getChatHistory,
  deleteChatHistory,
  getQuickResponse,
  getUserSettings,
  updateUserSettings,
  spendCredits,
  searchPlaces,
  checkUserFeedback
} from '../api';
import InsufficientCreditsModal from './InsufficientCreditsModal';
import ReactMarkdown from 'react-markdown';

import { LANGUAGES, RELIGIONS, COUNTRIES, MONTHS, DAYS, YEARS, PROMPTS } from '../constants/chatConstants';

import MessageBubble from './utils/MessageBubble';
import AnalysisButtons from './utils/AnalysisButtons';
import SuggestionBox from './utils/SuggestionBox';
import BirthDetailsModal from './utils/BirthDetailsModal';
import DatePickerModal from './utils/DatePickerModal';
import TimePickerModal from './utils/TimePickerModal';
import FeedbackNotification from './utils/FeedbackNotification';
import Dropdown from './utils/Dropdown';

function ChatBot() {
  const { 
    user, 
    deductCredits, 
    getReligionGreeting, 
    getReligionBlessing, 
    refreshCredits,
    d1ChartData,        // ✅ NEW: Get D1 chart data
    d1ChartLoading,     // ✅ NEW: Get loading state
    loadD1Chart         // ✅ NEW: Get load function
  } = useAuth();
  
  const navigate = useNavigate();

  // refs
  const messagesEndRef = useRef(null);
  const suggestionBoxRef = useRef(null);
  const settingsRef = useRef(null);
  const responseTypeRef = useRef(null);
  const isInitializing = useRef(false);
  const searchTimeoutRef = useRef(null);
  const analysisButtonsRef = useRef(null);

  // state
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);

  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [requiredCredits, setRequiredCredits] = useState(0);

  const [selectedLanguage, setSelectedLanguage] = useState(user?.settings?.Language || 'HINGLISH');
  const [userSettings, setUserSettings] = useState(null);
  const [responseType, setResponseType] = useState(() => user?.settings?.responseType || 'NORMAL');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showResponseTypeDropdown, setShowResponseTypeDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [showBirthDetailsPopup, setShowBirthDetailsPopup] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({
    name: 'India',
    code: 'in',
    flag: '🇮🇳'
  });

  const [birthDetails, setBirthDetails] = useState({
    full_name: '',
    religion: 'Hindu',
    birth_day: '',
    birth_month: '',
    birth_year: '',
    birthtime: '',
    birthplace: '',
    timezone: 'Asia/Kolkata'
  });

  const [tempBirthProfile, setTempBirthProfile] = useState(null);
  const [showFeedbackNotification, setShowFeedbackNotification] = useState(false);
  const [showTypeConfirmDialog, setShowTypeConfirmDialog] = useState(false);

  const [lastUserMessage, setLastUserMessage] = useState('');
  const [lastAssistantMessage, setLastAssistantMessage] = useState('');
  const [lastMessageType, setLastMessageType] = useState(null);
  const [selectedAnalysisType, setSelectedAnalysisType] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [hasFeedback, setHasFeedback] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // ✅ NEW: Load D1 chart when component mounts
  useEffect(() => {
    if (user?.userId && !d1ChartData && !d1ChartLoading) {
      console.log('📊 ChatBot: Loading D1 chart data...');
      loadD1Chart();
    }
  }, [user?.userId, d1ChartData, d1ChartLoading, loadD1Chart]);

  // helper: scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const closeOnOutsideClick = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionBoxRef.current && !suggestionBoxRef.current.contains(event.target)) {
        setFilteredSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Fetch settings
  const fetchUserSettings = useCallback(async () => {
    if (!user?.userId) {
      setSelectedLanguage('HINGLISH');
      setResponseType('NORMAL');
      return;
    }
    try {
      const response = await getUserSettings(user.userId);
      if (response.data) {
        setUserSettings(response.data);
        setSelectedLanguage(response.data.Language || 'HINGLISH');
        setResponseType(response.data.MessageType || 'NORMAL');
      } else {
        setSelectedLanguage('HINGLISH');
        setResponseType('NORMAL');
      }
    } catch (err) {
      console.error('Failed to fetch settings', err);
      setSelectedLanguage('HINGLISH');
      setResponseType('NORMAL');
    }
  }, [user]);

  useEffect(() => {
    if (user?.userId) fetchUserSettings();
  }, [user?.userId, fetchUserSettings]);

  // Load chat history
  const loadChatHistory = useCallback(async () => {
    if (!user?.id) return false;
    try {
      const response = await getChatHistory(user.id);
      if (response.data.success && response.data.data) {
        const grouped = response.data.data;
        const all = [];
        Object.keys(grouped).sort().forEach(dateKey => {
          grouped[dateKey].reverse().forEach(msg => {
            all.push({
              message: msg.message,
              direction: msg.direction,
              sender: msg.sender,
              timestamp: msg.timestamp,
              messageType: msg.messageType
            });
          });
        });
        if (all.length) {
          setMessages(all);
          const umsgs = all.filter(m => m.sender === 'user');
          const amsgs = all.filter(m => m.sender === 'assistant');
          if (umsgs.length) setLastUserMessage(umsgs[umsgs.length - 1].message);
          if (amsgs.length) setLastAssistantMessage(amsgs[amsgs.length - 1].message);
          setLastMessageType(PROMPTS.messageTypes.NQ);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error loading chat history', err);
      return false;
    }
  }, [user]);

  // Initialization: load history or free horoscope
  useEffect(() => {
    if (user?.userId && userSettings && !hasInitialized && !isInitializing.current) {
      isInitializing.current = true;
      (async () => {
        try {
          const hasHistory = await loadChatHistory();
          if (!hasHistory) {
            await generateFreeHoroscope();
          }
          setHasInitialized(true);
        } catch (err) {
          console.error('Initialization error', err);
          isInitializing.current = false;
        }
      })();
    }
  }, [user?.userId, userSettings, hasInitialized, loadChatHistory]);

  // check feedback after a few questions
  useEffect(() => {
    if (user?.userId) {
      (async () => {
        try {
          const r = await checkUserFeedback(user.userId);
          setHasFeedback(r.hasFeedback);
        } catch (err) {
          console.error('Failed to check feedback', err);
        }
      })();
    }
  }, [user?.userId]);

  // typing messages selection
  const getRandomTypingMessage = useCallback((religion) => {
    const key = religion?.toUpperCase?.() || 'HINDU';
    const msgs = PROMPTS.typing[key] || PROMPTS.typing.HINDU;
    if (Array.isArray(msgs) && msgs.length) return msgs[Math.floor(Math.random() * msgs.length)];
    return '🔮 AastroG is consulting the cosmic energies...';
  }, []);

  const generateFreeHoroscope = async () => {
    console.log('🔮 Generating free horoscope...');
    setIsTyping(true);

    try {
      const birthProfile = buildBirthProfile(user);
      const messageSettings = buildMessageSettings(PROMPTS.messageTypes.FH, 50);

      // ✅ Pass D1 chart data to getQuickResponse
      const response = await getQuickResponse(
        PROMPTS.template.HOROSCOPE_PROMPT,
        birthProfile,
        messageSettings,
        d1ChartData  // ✅ Pass D1 chart data
      );

      if (response.success) {
        const userName = user?.full_name || user?.given_name || 'Beta';
        const welcomeMessage = (PROMPTS.welcome[selectedLanguage] || PROMPTS.welcome.HINGLISH)
          .replace('{name}', userName);
        const fullMessage = `${welcomeMessage}\n\n${response.reply}`;

        push({
          message: fullMessage,
          direction: 'incoming',
          sender: 'assistant',
          timestamp: new Date().toISOString()
        });

        setLastUserMessage('Daily Horoscope');
        setLastAssistantMessage(fullMessage);
        setLastMessageType(PROMPTS.messageTypes.FH);
      }
    } catch (error) {
      console.error(' Error generating horoscope:', error);
      const welcomeMessage = PROMPTS.welcomeMessage[selectedLanguage] || PROMPTS.welcomeMessage.HINGLISH;
      push({
        message: welcomeMessage,
        direction: 'incoming',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsTyping(false);
    }
  };

  const getRandomGuruMessage = useCallback(() => {
    const religion = tempBirthProfile?.religion || user?.religion || 'Hindu';
    return getRandomTypingMessage(religion);
  }, [getRandomTypingMessage, tempBirthProfile, user]);

  // ✅ UPDATED: Build birth profile with D1 chart data flag
  const buildBirthProfile = useCallback((profile = user) => {
    const activeProfile = tempBirthProfile || profile;
    const isTempProfile = !!tempBirthProfile;

    console.log('🔍 Building birth profile:', {
      hasTempProfile: !!tempBirthProfile,
      tempProfileName: tempBirthProfile?.full_name,
      userProfileName: user?.full_name,
      usingProfile: activeProfile?.full_name || activeProfile?.name,
      hasD1Data: !!d1ChartData  // ✅ Log D1 data availability
    });

    return {
      name: activeProfile?.full_name || activeProfile?.given_name || activeProfile?.name || 'User',
      dob: activeProfile?.birth_date || activeProfile?.birthdate || 'unknown',
      time: activeProfile?.birth_time || activeProfile?.birthtime || 'unknown',
      timezone: activeProfile?.timezone || 'Asia/Kolkata',
      place: activeProfile?.birth_place || activeProfile?.birthplace || 'unknown',
      latLong: (activeProfile?.latitude && activeProfile?.longitude) 
        ? `${activeProfile.latitude}, ${activeProfile.longitude}` 
        : 'unknown',
      religion: activeProfile?.religion || 'Hindu',
      full_name: activeProfile?.full_name || activeProfile?.name || 'User',
      birth_date: activeProfile?.birth_date || activeProfile?.birthdate,
      birth_time: activeProfile?.birth_time || activeProfile?.birthtime,
      birth_place: activeProfile?.birth_place || activeProfile?.birthplace,
      latitude: activeProfile?.latitude,
      longitude: activeProfile?.longitude,
      isTempProfile: isTempProfile,
      hasD1Chart: !!d1ChartData  // ✅ Include D1 chart availability
    };
  }, [user, tempBirthProfile, d1ChartData]);

  // Reusable message push
  const push = useCallback((msg) => setMessages(prev => [...prev, msg]), []);

  // Unified sendMessage function
  const getCreditsByResponseType = useCallback((rt) => {
    switch (rt) {
      case 'QUICK': return { spendTypeId: 1, creditsRequired: 10 };
      case 'DETAILED': return { spendTypeId: 2, creditsRequired: 20 };
      default: return { spendTypeId: 3, creditsRequired: 15 };
    }
  }, []);

  const buildMessageSettings = useCallback((questionType, maxWords = null) => {
    const maxResponseLength = maxWords || (questionType === PROMPTS.messageTypes.TL ? 250 : (PROMPTS.responseLength[responseType]?.maxWords || 150));
    return {
      questionType,
      language: selectedLanguage,
      maxResponseLength
    };
  }, [responseType, selectedLanguage]);

  // ✅ UPDATED: sendMessage - now passes D1 chart data
  const sendMessage = useCallback(async ({
    userMessage,
    messageType,
    aiContextMessage = null,
    queryProfile = null,
    customMaxWords = null,
    clearHistory = false
  }) => {
    try {
      const birthProfile = buildBirthProfile(user);
      const messageSettings = buildMessageSettings(messageType, customMaxWords);
      const { spendTypeId, creditsRequired } = getCreditsByResponseType(responseType);

      if ((user?.credits || 0) < creditsRequired) {
        setRequiredCredits(creditsRequired);
        setShowInsufficientModal(true);
        setIsTyping(false);
        if (clearHistory) setMessages([]);
        else setMessages(prev => prev.slice(0, -1));
        setInputValue(userMessage);
        return null;
      }

      // ✅ Pass D1 chart data to backend
      const response = await saveChatMessage(
        user.id,
        userMessage,
        birthProfile,
        messageSettings,
        aiContextMessage || userMessage,
        queryProfile,
        d1ChartData  // ✅ Pass D1 chart data
      );

      if (!response || !response.success) return null;

      setLastUserMessage(userMessage);
      setLastAssistantMessage(response.reply);
      setLastMessageType(messageType);

      setQuestionCount(prev => {
        const newCount = prev + 1;
        if (newCount === 3) {
          checkUserFeedback(user.userId)
            .then(result => {
              if (!result.hasFeedback) setTimeout(() => setShowFeedbackNotification(true), 2000);
            })
            .catch(() => setTimeout(() => setShowFeedbackNotification(true), 2000));
        }
        return newCount;
      });

      await spendCredits(user.userId, spendTypeId, `${messageType}: ${userMessage.substring(0, 50)}`, null);
      await refreshCredits();
      return response;
    } catch (err) {
      console.error('Error in sendMessage', err);
      push({
        message: 'Sorry, something went wrong. Please try again.',
        direction: 'incoming',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
      return null;
    }
  }, [
    buildBirthProfile, 
    buildMessageSettings, 
    getCreditsByResponseType, 
    refreshCredits, 
    user, 
    push, 
    setMessages,
    d1ChartData  // ✅ Add to dependencies
  ]);

  const handleSettingChange = useCallback(async (settingKey, settingValue) => {
    if (!userSettings?.SettingId) return;

    // Update local state based on setting type
    if (settingKey === 'Language') {
      setSelectedLanguage(settingValue);
      setShowLanguageDropdown(false);
    } else if (settingKey === 'MessageType') {
      setResponseType(settingValue);
      setShowResponseTypeDropdown(false);
    }

    try {
      await updateUserSettings(
        userSettings.SettingId,
        { [settingKey]: settingValue, ModifiedBy: user?.full_name || 'User' }
      );
      await fetchUserSettings();
    } catch (err) {
      console.error(`Failed to update ${settingKey}`, err);
      await fetchUserSettings();
    }
  }, [userSettings, user, fetchUserSettings]);

  const handleInputWithSuggestions = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    if (value.trim().length >= 2) {
      const filtered = (PROMPTS.suggestions[selectedLanguage] || [])
        .filter(s => s.toLowerCase().includes(value.toLowerCase()));
      setFilteredSuggestions(filtered.slice(0, 10));
    } else {
      setFilteredSuggestions([]);
    }
  }, [selectedLanguage]);

  const handleSuggestionClick = useCallback((s) => {
    setInputValue(s);
    setFilteredSuggestions([]);
    setTimeout(() => {
      document.querySelector('.chat-input')?.focus();
    }, 0);
  }, []);

  const handlePlaceSelect = useCallback((place) => {
    try {
      let formattedPlace = place.description || place.formatted_address || `${place.main_text || ''} ${place.secondary_text || ''}`.trim();
      setBirthDetails(prev => ({ ...prev, birth_place: formattedPlace }));
      setShowSuggestions(false);
      setTimeout(() => setPlaceSuggestions([]), 200);
    } catch (err) {
      console.error('Error selecting place', err);
      setPlaceSuggestions([]);
    }
  }, []);

  const handleSearchQueryChange = useCallback((value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value || value.length < 2) {
      setPlaceSuggestions([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setIsLoadingPlaces(true);
      try {
        const resp = await searchPlaces(value, selectedCountry.code.toUpperCase());
        if (resp.data && resp.data.success) setPlaceSuggestions(resp.data.places || []);
        else setPlaceSuggestions([]);
      } catch (err) {
        console.error('Place search error', err);
        setPlaceSuggestions([]);
      } finally {
        setIsLoadingPlaces(false);
      }
    }, 300);
  }, [selectedCountry]);

  // draft handling (localStorage)
  useEffect(() => {
    if (user?.id) {
      const key = `astroguru_draft_${user.id}`;
      const saved = localStorage.getItem(key);
      if (saved) setInputValue(saved);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && inputValue.trim()) {
      const key = `astroguru_draft_${user.id}`;
      localStorage.setItem(key, inputValue);
    }
  }, [inputValue, user?.id]);

  const clearDraft = useCallback(() => {
    if (user?.id) {
      localStorage.removeItem(`astroguru_draft_${user.id}`);
      setInputValue('');
    }
  }, [user?.id]);

  // Simplified handlers for send
  async function handleSendWithType(forcedMessageType) {
    if (!inputValue.trim()) return;
    const originalUserMessage = inputValue.trim();
    if (user?.id) localStorage.removeItem(`astroguru_draft_${user.id}`);

    const messageType = forcedMessageType;
    let aiContextMessage = originalUserMessage;
    let dbMessage = originalUserMessage;

    if (messageType === PROMPTS.messageTypes.FLUP && lastUserMessage && lastAssistantMessage) {
      aiContextMessage = `Previous Question: "${lastUserMessage}"\nPrevious Response: "${lastAssistantMessage.substring(0, 200)}..."\n\nFollow-up Question: ${originalUserMessage}`;
    }

    const userMessage = {
      message: originalUserMessage,
      direction: 'outgoing',
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    if (messageType === PROMPTS.messageTypes.NQ) setMessages([userMessage]); else push(userMessage);

    setInputValue('');
    setIsTyping(true);
    setFilteredSuggestions([]);

    const response = await sendMessage({
      userMessage: dbMessage,
      messageType,
      aiContextMessage,
      queryProfile: null,
      clearHistory: messageType === PROMPTS.messageTypes.NQ
    });

    if (response) {
      push({
        message: response.reply,
        direction: 'incoming',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
      setSelectedAnalysisType(null);
    }
    setIsTyping(false);
  }

  async function handleSend() {
    if (!inputValue.trim()) return;

    const originalUserMessage = inputValue.trim();
    if (user?.id) localStorage.removeItem(`astroguru_draft_${user.id}`);

    let messageType = selectedAnalysisType;
    if (!messageType) {
      if (!lastMessageType || lastMessageType === PROMPTS.messageTypes.FH) {
        messageType = PROMPTS.messageTypes.NQ;
        setSelectedAnalysisType(messageType);
      } else {
        setShowTypeConfirmDialog(true);
        return;
      }
    }

    let aiContextMessage = originalUserMessage;
    let dbMessage = originalUserMessage;
    if (messageType === PROMPTS.messageTypes.FLUP && lastUserMessage && lastAssistantMessage) {
      aiContextMessage = `Previous Question: "${lastUserMessage}"\nPrevious Response: "${lastAssistantMessage.substring(0, 200)}..."\n\nFollow-up Question: ${originalUserMessage}`;
    }

    const userMessage = {
      message: originalUserMessage,
      direction: 'outgoing',
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    if (messageType === PROMPTS.messageTypes.NQ) setMessages([userMessage]); else push(userMessage);

    setInputValue('');
    setIsTyping(true);
    setFilteredSuggestions([]);

    const response = await sendMessage({
      userMessage: dbMessage,
      messageType,
      aiContextMessage,
      queryProfile: null,
      clearHistory: messageType === PROMPTS.messageTypes.NQ
    });

    if (response) {
      push({
        message: response.reply,
        direction: 'incoming',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
      setSelectedAnalysisType(null);
    }
    setIsTyping(false);
  }

  const handleDirectAnalysis = async (analysisType) => {
    if (!lastUserMessage || !lastAssistantMessage) {
      alert('No previous conversation found to analyze. Please ask a question first.');
      return;
    }
    setIsTyping(true);

    const dbMessage = analysisType === PROMPTS.messageTypes.RM ? 'Show me remedies' : 'Show me technical analysis';
    push({
      message: dbMessage,
      direction: 'outgoing',
      sender: 'user',
      timestamp: new Date().toISOString()
    });

    const cleanMessageForDB = analysisType === PROMPTS.messageTypes.RM
      ? 'Please provide specific remedies for the above.'
      : 'Please provide detailed technical analysis for the above.';

    const aiContextMessage = `Previous Question: "${lastUserMessage}"\nPrevious Response: "${lastAssistantMessage.substring(0, 200)}..." \n${cleanMessageForDB}`;

    const response = await sendMessage({
      userMessage: dbMessage,
      messageType: analysisType,
      aiContextMessage,
      queryProfile: buildBirthProfile(user),
      customMaxWords: analysisType === PROMPTS.messageTypes.TL ? 250 : null
    });

    if (response) {
      push({
        message: response.reply,
        direction: 'incoming',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
    }
    setIsTyping(false);
  };

  const handleClearChat = async () => {
    if (!window.confirm('Are you sure you want to clear all messages? This will permanently delete your chat history from the database.')) return;
    try {
      const response = await deleteChatHistory(user.id);
      if (response.data.success) {
        setMessages([]);
        setTempBirthProfile(null);
        sessionStorage.removeItem('astroguru_temp_profile');
        if (user?.id) localStorage.removeItem(`astroguru_first_question_${user.id}`);
        setSelectedAnalysisType(PROMPTS.messageTypes.NQ);
        setLastMessageType(null);
        setLastUserMessage('');
        setLastAssistantMessage('');
        setShowSettings(false);
      }
    } catch (err) {
      console.error('Error deleting chat history', err);
      alert('Failed to delete chat history. Please try again.');
    }
  };

  const handleGetCredits = () => navigate('/credits');
  const handleCloseInsufficientModal = () => setShowInsufficientModal(false);

  // short memoized suggestion list for UI
  const suggestionList = useMemo(() => {
    return inputValue.trim().length >= 2
      ? (PROMPTS.suggestions[selectedLanguage] || [])
        .filter(s => s.toLowerCase().includes(inputValue.toLowerCase()))
        .slice(0, 10)
      : [];
  }, [inputValue, selectedLanguage]);

  // render
  return (
    <div className="chatbot-container">
      {user?.credits < 10 && (
        <div className="credit-warning" onClick={handleGetCredits}>
          <span>⚠️ Low Credits! You have only {user?.credits} credits left, {user?.given_name || 'beta'}. Click to get more! 💎</span>
        </div>
      )}

      {/* ✅ D1 Chart Loading Indicator */}
      {/* {d1ChartLoading && (
        <div style={{ 
          padding: '8px', 
          background: '#f0f8ff', 
          textAlign: 'center',
          fontSize: '12px',
          borderBottom: '1px solid #ddd'
        }}>
          📊 Loading birth chart data...
        </div>
      )} */}

      <div className="chat-area">
        <div className="chat-header">
          <span>{PROMPTS.greetings[selectedLanguage] || PROMPTS.greetings.HINGLISH}</span>

          <div className="settings-container">
            <div className="settings-row" ref={settingsRef}>

              {showSettings && (
                <div className="settings-panel">
                  {/* Ask for Other Button */}
                  <button className="language-toggle" onClick={() => setShowBirthDetailsPopup(true)}>
                    <span className="ask-icon">👤</span>
                    <span className="ask-text">Ask for Other</span>
                  </button>

                  {/* Language Dropdown */}
                  <div className="language-dropdown-container">
                    <Dropdown
                      variant="chatbot"
                      items={[
                        { label: 'English', value: 'ENGLISH' },
                        { label: 'हिंदी (Hindi)', value: 'HINDI' },
                        { label: 'HINGLISH (Hindi + English)', value: 'HINGLISH' }
                      ]}
                      value={selectedLanguage}
                      onChange={(val) => handleSettingChange('Language', val)}
                      placeholder="Select Language"
                    />
                  </div>
                  
                  {/* Response Type Dropdown */}
                  <div className="language-dropdown-container" ref={responseTypeRef}>
                    <Dropdown
                      variant="chatbot"
                      items={[
                        { label: '⚡ Quick', value: 'QUICK' },
                        { label: '💬 Normal', value: 'NORMAL' },
                        { label: '📚 Detailed', value: 'DETAILED' }
                      ]}
                      value={responseType}
                      onChange={(val) => handleSettingChange('MessageType', val)}
                      placeholder="Select Type"
                    />
                  </div>

                  {/* Clear Chat Button */}
                  <button className="clear-chat-toggle" onClick={handleClearChat}>
                    <span className="clear-icon">🗑️</span>
                    <span className="clear-text">Clear All</span>
                  </button>

                  {/* Temp Profile Indicator */}
                  {tempBirthProfile && (
                    <div className="temp-profile-indicator">
                      <span className="temp-profile-icon">🎭</span>
                      <span className="temp-profile-text">
                        Using Profile: {tempBirthProfile.full_name} ({tempBirthProfile.religion})
                      </span>
                      <button
                        className="temp-profile-clear"
                        onClick={() => {
                          setTempBirthProfile(null);
                          sessionStorage.removeItem('astroguru_temp_profile');
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}
              <button
                className={`settings-toggle ${showSettings ? 'active' : ''}`}
                onClick={() => setShowSettings(!showSettings)}
              >
                <span className="settings-icon">⚙️</span>
                <span className="settings-text">Settings</span>
                <span className={`settings-arrow ${showSettings ? 'open' : ''}`}>▼</span>
              </button>
            </div>
          </div>
        </div>

        <div className="messages-container">
          {messages.map((m, i) => <MessageBubble key={i} msg={m} />)}
          <div ref={messagesEndRef} />

          {messages.length > 0 && messages[messages.length - 1].sender === 'assistant' && lastMessageType !== PROMPTS.messageTypes.FH && (
            <div className="message-bubble incoming" ref={analysisButtonsRef}>
              <AnalysisButtons
                selectedAnalysisType={selectedAnalysisType}
                setSelectedAnalysisType={setSelectedAnalysisType}
                handleDirectAnalysis={handleDirectAnalysis}
                PROMPTS={PROMPTS}
                selectedLanguage={selectedLanguage}
              />
            </div>
          )}

          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-bubble">{getRandomGuruMessage()}</div>
            </div>
          )}
        </div>

        <div className="input-area" ref={suggestionBoxRef}>
          <input
            type="text"
            value={inputValue}
            onChange={handleInputWithSuggestions}
            onKeyPress={(e) => { if (e.key === 'Enter' && !suggestionList.length) handleSend(); }}
            placeholder={PROMPTS.languages[selectedLanguage]?.PLACEHOLDER}
            className="chat-input"
            autoComplete="off"
          />

          <SuggestionBox suggestions={suggestionList} onSelect={handleSuggestionClick} />

          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isTyping}
            className={`send-button ${inputValue.trim() && !isTyping ? 'active' : 'inactive'}`}
          >
            Send
          </button>
        </div>

        <FeedbackNotification 
          show={showFeedbackNotification} 
          onClose={() => setShowFeedbackNotification(false)} 
          onGiveFeedback={() => { 
            setShowFeedbackNotification(false); 
            navigate('/feedback'); 
          }} 
        />

        {showBirthDetailsPopup && (
          <BirthDetailsModal
            show={showBirthDetailsPopup}
            close={() => setShowBirthDetailsPopup(false)}
            birthDetails={birthDetails}
            setBirthDetails={setBirthDetails}
            RELIGIONS={RELIGIONS}
            COUNTRIES={COUNTRIES}
            MONTHS={MONTHS}
            DAYS={DAYS}
            YEARS={YEARS}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            handleSearchQueryChange={handleSearchQueryChange}
            placeSuggestions={placeSuggestions}
            isLoadingPlaces={isLoadingPlaces}
            handlePlaceSelect={handlePlaceSelect}
            setShowDatePicker={setShowDatePicker}
            setShowTimePicker={setShowTimePicker}
            setTempBirthProfile={setTempBirthProfile}
          />
        )}

        {showDatePicker && (
          <DatePickerModal 
            show={showDatePicker} 
            close={() => setShowDatePicker(false)} 
            birthDetails={birthDetails} 
            setBirthDetails={setBirthDetails} 
            DAYS={DAYS} 
            MONTHS={MONTHS} 
            YEARS={YEARS} 
          />
        )}

        {showTimePicker && (
          <TimePickerModal 
            show={showTimePicker} 
            close={() => setShowTimePicker(false)} 
            birthDetails={birthDetails} 
            setBirthDetails={setBirthDetails} 
          />
        )}

      </div>

      {/* Type Confirmation Dialog */}
      {showTypeConfirmDialog && (
        <div className="feedback-notification-overlay">
          <div className="feedback-notification">
            <div className="feedback-icon">⚠️</div>

            <h3 className="feedback-title">Message Type Not Selected</h3>

            <p className="feedback-description">
              You haven't selected a message type yet. Message types help AastroG
              understand your question better.
              <br /><br />
              <strong>Would you like to proceed with "Follow-up"?</strong>
              <br />
              <small style={{ opacity: 0.8, fontSize: '13px' }}>
                (Follow-up continues your previous conversation)
              </small>
            </p>

            <div className="feedback-actions">
              <button
                className="feedback-btn feedback-btn-primary"
                onClick={() => {
                  setSelectedAnalysisType(PROMPTS.messageTypes.FLUP);
                  setShowTypeConfirmDialog(false);
                  setTimeout(() => handleSendWithType(PROMPTS.messageTypes.FLUP), 100);
                }}
              >
                Yes, Follow-up
              </button>

              <button
                className="feedback-btn feedback-btn-secondary"
                onClick={() => {
                  setShowTypeConfirmDialog(false);
                  setTimeout(() => {
                    analysisButtonsRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center'
                    });
                  }, 100);
                }}
              >
                Let Me Choose
              </button>
            </div>
          </div>
        </div>
      )}

      <InsufficientCreditsModal
        isOpen={showInsufficientModal}
        onClose={handleCloseInsufficientModal}
        requiredCredits={requiredCredits}
        currentCredits={user?.credits || 0}
      />
    </div>
  );
}

export default ChatBot;
